import { Fragment, useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'

import Gallery from '../components/Gallery/Gallery'
import EmbeddedVideo from '../components/VideoPlayer/EmbeddedVideo'
import Events from '../components/Cities/Events'
import { Resources } from '../components/Resources'
import { ResourceList } from '../components/ResourceList'
import { UnRecommendations } from '../components/UnRecommendations'
import { RallyDemands } from '../components/RallyDemands'
import type { Rally as RallyData } from '../data/rallies'

// Page for a rally in data/rallies.ts. Based on views/Action.tsx (the Dec '25 rally).
export default function Rally({ rally }: { rally: RallyData }) {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()

  const sizeClass1 =
    i18n.language === 'fr' || i18n.language === 'da'
      ? `wrap-break-word inline-block text-4xl sm:text-6xl md:text-6xl lg:text-7xl xl:text-9xl`
      : 'text-6xl md:text-8xl xl:text-9xl'

  useEffect(() => {
    if (location.state?.scrollToId) {
      const element = document.getElementById(location.state.scrollToId)
      if (element) {
        const isMobile = window.matchMedia('(max-width: 768px)').matches
        const offset = isMobile ? window.innerHeight * 0.1 : window.innerHeight * 0.8
        const top = element.getBoundingClientRect().top + window.scrollY + offset
        window.scrollTo({ top, behavior: 'smooth' })
      }
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, navigate, location.pathname])

  return (
    <div className='min-h-screen w-full text-gray-900 flex flex-col text-lg'>
      <section className='flex flex-col justify-center items-center w-full min-h-[75vh] text-center px-4 bg-gray-100 pb-10 pt-4 gap-8'>
        <h1 className='text-left md:text-center font-medium opacity-90 mb-4 wrap-break-word flex flex-col gap-4 text-6xl md:text-7xl'>
          <span dangerouslySetInnerHTML={{ __html: t(`${rally.i18n}.heroStop`) }}></span>
          <div className='bg-black p-[0.5px] w-[80%] md:self-center'></div>
          <span>{t(`${rally.i18n}.heroSave`)}</span>
        </h1>
        <p
          className={`inline-flex ${sizeClass1} font-semibold flex-wrap text-right items-center justify-end md:text-center text-blue-500 gap-4 mb-3`}
          dangerouslySetInnerHTML={{ __html: t(`${rally.i18n}.worldwideRally`) }}
        ></p>
        <h2 className='text-3xl md:text-4xl mt-12 font-semibold mb-3 uppercase'>
          {t(`${rally.i18n}.heroDate`)}
        </h2>
        <p className='text-lg opacity-80'>{rally.hashtags}</p>
      </section>

      <section className='px-6 md:px-20 py-12 pb-20 text-center text-2xl leading-relaxed opacity-90 bg-gray-50 flex flex-col justify-center items-center lg:flex-row'>
        {rally.video ? (
          <div className='w-full pb-8 lg:pb-0 lg:w-[50%] shrink-0'>
            <EmbeddedVideo
              video={rally.video}
              title={t('leftBank.documentaryTitle')}
              className=''
              autoplay
            />
          </div>
        ) : (
          <img
            src={rally.image}
            alt={rally.imageAltKey ? t(rally.imageAltKey) : 'human safari drone'}
            className='pb-8 lg:pb-0 lg:w-[50%]' />
        )}
        <p className='px-4'>
          {rally.infoKeys.map((key, i) => (
            <Fragment key={key}>
              {i > 0 && <br />}
              <Trans i18nKey={key} components={{ bold: <strong /> }}></Trans>
            </Fragment>
          ))}
        </p>
      </section>

      {!!rally.posters?.length && (
        <div className='bg-blue-800 w-full text-gray-100 p-3 uppercase flex justify-center text-center text-xl md:text-2xl font-bold'>
          <button
            onClick={() => {
              const el = document.getElementById('posters-gallery')
              if (el) el.scrollIntoView({ behavior: 'smooth' })
            }}
            className='rounded-full text-blue-800 bg-white px-6 py-3 shadow-lg hover:bg-blue-700 cursor-pointer transition'
          >
            {t(`${rally.i18n}.seeGallery`)}
          </button>
        </div>
      )}

      {rally.photos && (
        <Gallery
          id='rallies'
          images={rally.photos}
          rally={rally}
          collapseToFirstRow={true}
          height='aspect-2/2'
        />
      )}

      <Events rally={rally} />

      {rally.demands ? (
        <RallyDemands titleKey={rally.demands.titleKey} itemKeys={rally.demands.itemKeys} />
      ) : (
        <UnRecommendations />
      )}

      {rally.resources ? (
        // skip the video already shown at the top of the page
        <ResourceList
          groups={rally.resources.filter(
            (g) => !g.video || g.video.id !== rally.video?.id
          )}
        />
      ) : (
        <Resources />
      )}
      {!!rally.posters?.length && <Gallery id='posters' images={rally.posters} rally={rally} downloadable />}
    </div>
  )
}
